import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, qs } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import {
  CARE_KIND_LABEL,
  CASE_CHANNEL_LABEL,
  IMPORT_RESULT_LABEL,
  SATISFACTION_LABEL,
  SUGGESTION_REASON_LABEL,
  label,
} from '../lib/labels.js';
import { dateTime, num } from '../lib/format.js';
import {
  Confirm,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  TabBar,
} from '../components/ui.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';

type Tab = 'cases' | 'flags' | 'suggestions' | 'import';

interface CaseRow {
  id: number;
  contact_id: string | null;
  brand: string | null;
  race_name: string | null;
  order_code: string | null;
  channel: string;
  topic: string | null;
  status: string;
  resolution: string | null;
  satisfaction: string;
  source: string;
  opened_at: string;
  email: string | null;
  owner_name: string | null;
}
interface FlagRow {
  id: number;
  contact_id: string;
  brand: string;
  race_id: string | null;
  race_name: string | null;
  kind: string;
  note: string | null;
  set_at: string;
  expires_at: string | null;
  cleared_at: string | null;
  email: string;
}
interface SuggestionRow {
  id: number;
  contact_id: string;
  brand: string;
  race_id: string | null;
  race_name: string | null;
  reason: string;
  created_at: string;
  email: string;
}

const satTone = (s: string): 'ok' | 'warn' | 'bad' | 'neu' =>
  s === 'satisfied' ? 'ok' : s === 'dissatisfied' ? 'bad' : s === 'neutral' ? 'neu' : 'neu';

export function Care({ me, rights }: { me: Me; rights: Rights }): JSX.Element {
  const [tab, setTab] = useState<Tab>('cases');
  const canManage = rights.can('manage_care');
  return (
    <>
      <PageHeader
        title="Zákaznická péče"
        description="Evidence případů zákazníků a označení, která drží marketingové zprávy před závodem. Poznámky jsou osobní údaje, vidí je jen péče a administrátor."
      />
      {!canManage ? <p className="hint">Obrazovku máte jen ke čtení.</p> : null}
      <TabBar<Tab>
        value={tab}
        onChange={setTab}
        tabs={[
          { id: 'cases', label: 'Případy', hint: 'jak jsme to řešili' },
          { id: 'flags', label: 'Označení', hint: 'komu držíme zprávy' },
          { id: 'suggestions', label: 'Návrhy', hint: 'co hub doporučuje označit' },
          { id: 'import', label: 'Import případů', hint: 'ze souboru CSV' },
        ]}
      />
      {tab === 'cases' ? <Cases me={me} canManage={canManage} /> : null}
      {tab === 'flags' ? <Flags me={me} canManage={canManage} /> : null}
      {tab === 'suggestions' ? <Suggestions canManage={canManage} /> : null}
      {tab === 'import' ? <ImportCases canManage={canManage} /> : null}
    </>
  );
}

function Cases({ me, canManage }: { me: Me; canManage: boolean }): JSX.Element {
  const client = useQueryClient();
  const [status, setStatus] = useState('open');
  const [sat, setSat] = useState('');
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState({
    email: '',
    orderCode: '',
    channel: 'phone',
    topic: '',
    status: 'open',
    satisfaction: 'unknown',
    resolution: '',
  });

  const list = useQuery<{ items: CaseRow[]; total: number }>({
    queryKey: ['care-cases', status, sat, search],
    queryFn: () => api.get(`/api/care/cases${qs({ status, satisfaction: sat, q: search })}`),
  });
  const refresh = (): void => void client.invalidateQueries({ queryKey: ['care-cases'] });
  const create = useMutation({
    mutationFn: () =>
      api.post('/api/care/cases', {
        ...(form.email ? { email: form.email } : {}),
        ...(form.orderCode ? { orderCode: form.orderCode } : {}),
        channel: form.channel,
        ...(form.topic ? { topic: form.topic } : {}),
        status: form.status,
        satisfaction: form.satisfaction,
        ...(form.resolution ? { resolution: form.resolution } : {}),
      }),
    onSuccess: () => {
      setError(null);
      setForm({ ...form, email: '', orderCode: '', topic: '', resolution: '' });
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });
  const update = useMutation({
    mutationFn: (i: { id: number; status?: string; satisfaction?: string; resolution?: string }) =>
      api.patch(`/api/care/cases/${i.id}`, i),
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => setError(e.message),
  });

  return (
    <>
      {error ? <ErrorState title="Změnu se nepodařilo uložit" error={new Error(error)} /> : null}
      <Panel title={`Případy (${num(list.data?.total ?? 0)})`}>
        <div className="s2a-filters">
          <Field label="Stav případu">
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="open">Otevřené</option>
              <option value="resolved">Vyřešené</option>
              <option value="">Všechny</option>
            </select>
          </Field>
          <Field label="Spokojenost zákazníka">
            <select value={sat} onChange={(e) => setSat(e.target.value)}>
              <option value="">Jakákoli</option>
              {Object.entries(SATISFACTION_LABEL).map(([v, t]) => (
                <option key={v} value={v}>
                  {t}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Hledat případ">
            <input
              type="search"
              placeholder="E-mail nebo kód objednávky"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Field>
        </div>
        {list.isLoading ? <LoadingState what="případy" /> : null}
        {list.isError ? (
          <ErrorState
            title="Případy se nepodařilo načíst"
            error={list.error}
            onRetry={() => void list.refetch()}
          />
        ) : null}
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Zákazník</th>
                <th>Téma</th>
                <th>Kanál</th>
                <th>Stav</th>
                <th>Spokojenost</th>
                <th>Řešení</th>
                <th>Založeno</th>
                {canManage ? <th>Akce</th> : null}
              </tr>
            </thead>
            <tbody>
              {list.data?.items.map((c) => (
                <tr key={c.id} data-testid={`case-${c.id}`}>
                  <td>
                    {c.contact_id ? (
                      <a href={hrefFor('customers', c.contact_id)}>{c.email}</a>
                    ) : (
                      <span className="muted">nenapárováno</span>
                    )}
                    <div className="muted">
                      {c.brand ?? '—'} {c.order_code ?? ''} {c.race_name ?? ''}
                    </div>
                  </td>
                  <td>{c.topic ?? '—'}</td>
                  <td>{label(CASE_CHANNEL_LABEL, c.channel)}</td>
                  <td>
                    <StatusBadge
                      tone={c.status === 'open' ? 'warn' : 'ok'}
                      icon={c.status === 'open' ? 'clock' : 'check'}
                    >
                      {c.status === 'open' ? 'Otevřený' : 'Vyřešený'}
                    </StatusBadge>
                  </td>
                  <td>
                    <StatusBadge
                      tone={satTone(c.satisfaction)}
                      icon={
                        c.satisfaction === 'satisfied'
                          ? 'check'
                          : c.satisfaction === 'dissatisfied'
                            ? 'alert'
                            : 'info'
                      }
                    >
                      {label(SATISFACTION_LABEL, c.satisfaction)}
                    </StatusBadge>
                  </td>
                  <td className="muted">{c.resolution ?? '—'}</td>
                  <td>
                    {dateTime(c.opened_at, me.defaultTimezone)}
                    <div className="muted">{c.source}</div>
                  </td>
                  {canManage ? (
                    <td>
                      <div className="row">
                        {c.status === 'open' ? (
                          <button
                            className="btn"
                            onClick={() => update.mutate({ id: c.id, status: 'resolved' })}
                          >
                            Vyřešit
                          </button>
                        ) : (
                          <button
                            className="btn"
                            onClick={() => update.mutate({ id: c.id, status: 'open' })}
                          >
                            Znovu otevřít
                          </button>
                        )}
                        <select
                          aria-label={`Spokojenost případu ${c.id}`}
                          value={c.satisfaction}
                          onChange={(e) =>
                            update.mutate({ id: c.id, satisfaction: e.target.value })
                          }
                        >
                          {Object.entries(SATISFACTION_LABEL).map(([v, t]) => (
                            <option key={v} value={v}>
                              {t}
                            </option>
                          ))}
                        </select>
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {list.data && list.data.items.length === 0 ? (
          <EmptyState title="Žádný případ neodpovídá">
            {canManage
              ? 'Zkuste změnit filtr, nebo založte nový případ ve formuláři níže.'
              : 'Zkuste změnit filtr nebo hledaný text.'}
          </EmptyState>
        ) : null}
      </Panel>

      {canManage ? (
        <Panel
          title="Nový případ"
          subtitle="Ruční záznam (telefon, chat, sociální sítě). Zákazníka najdeme podle e-mailu nebo kódu objednávky, stačí jedno z nich."
        >
          <div className="s2a-form">
            <Field label="E-mail zákazníka">
              <input
                type="text"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </Field>
            <Field label="Kód objednávky">
              <input
                type="text"
                value={form.orderCode}
                onChange={(e) => setForm({ ...form, orderCode: e.target.value })}
              />
            </Field>
            <Field label="Kanál">
              <select
                value={form.channel}
                onChange={(e) => setForm({ ...form, channel: e.target.value })}
              >
                {Object.entries(CASE_CHANNEL_LABEL).map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Téma">
              <input
                type="text"
                value={form.topic}
                onChange={(e) => setForm({ ...form, topic: e.target.value })}
              />
            </Field>
          </div>
          <div className="s2a-form">
            <Field label="Stav případu">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="open">Otevřený</option>
                <option value="resolved">Vyřešený</option>
              </select>
            </Field>
            <Field label="Spokojenost zákazníka">
              <select
                value={form.satisfaction}
                onChange={(e) => setForm({ ...form, satisfaction: e.target.value })}
              >
                {Object.entries(SATISFACTION_LABEL).map(([v, t]) => (
                  <option key={v} value={v}>
                    {t}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Jak se vyřešilo">
              <input
                type="text"
                value={form.resolution}
                onChange={(e) => setForm({ ...form, resolution: e.target.value })}
              />
            </Field>
            <button
              className="btn pri"
              disabled={(!form.email && !form.orderCode) || create.isPending}
              onClick={() => create.mutate()}
            >
              Založit případ
            </button>
          </div>
        </Panel>
      ) : null}
    </>
  );
}

function Flags({ me, canManage }: { me: Me; canManage: boolean }): JSX.Element {
  const client = useQueryClient();
  const [all, setAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reason, setReason] = useState<Record<number, string>>({});
  const list = useQuery<{ items: FlagRow[] }>({
    queryKey: ['care-flags', all],
    queryFn: () => api.get(`/api/care/flags${qs({ status: all ? 'all' : 'active' })}`),
  });
  const clear = useMutation({
    mutationFn: (i: { id: number; reason: string }) =>
      api.post(`/api/care/flags/${i.id}/clear`, { reason: i.reason }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['care-flags'] });
    },
    onError: (e: Error) => setError(e.message),
  });
  return (
    <Panel
      title="Označení zákazníků"
      subtitle="Blokující označení drží marketingové zprávy k závodu, dokud ho někdo nezruší. Servisní zprávy (například vstupenky) odcházejí dál. Označit lze i z karty zákazníka nebo z návrhu."
      action={
        <label className="row">
          <input type="checkbox" checked={all} onChange={(e) => setAll(e.target.checked)} />
          Zobrazit i zrušená
        </label>
      }
    >
      {error ? <ErrorState title="Označení se nepodařilo změnit" error={new Error(error)} /> : null}
      {list.isLoading ? <LoadingState what="označení" /> : null}
      {list.isError ? (
        <ErrorState
          title="Označení se nepodařilo načíst"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : null}
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Zákazník</th>
              <th>Druh</th>
              <th>Rozsah</th>
              <th>Poznámka</th>
              <th>Od</th>
              <th>Do</th>
              {canManage ? <th>Zrušení</th> : null}
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((f) => (
              <tr key={f.id} data-testid={`flag-${f.id}`}>
                <td>
                  <a href={hrefFor('customers', f.contact_id)}>{f.email}</a>
                  <div className="muted">{f.brand}</div>
                </td>
                <td>
                  <StatusBadge tone={f.kind === 'vip' ? 'neu' : 'warn'} icon="flag">
                    {label(CARE_KIND_LABEL, f.kind)}
                  </StatusBadge>
                </td>
                <td>{f.race_name ?? 'všechny závody značky'}</td>
                <td className="muted">{f.note ?? '—'}</td>
                <td>{dateTime(f.set_at, me.defaultTimezone)}</td>
                <td>
                  {f.cleared_at
                    ? `zrušeno ${dateTime(f.cleared_at, me.defaultTimezone)}`
                    : f.expires_at
                      ? dateTime(f.expires_at, me.defaultTimezone)
                      : 'bez omezení'}
                </td>
                {canManage ? (
                  <td>
                    {f.cleared_at ? null : (
                      <div className="row">
                        <input
                          type="text"
                          placeholder="Důvod zrušení"
                          aria-label={`Důvod zrušení ${f.id}`}
                          value={reason[f.id] ?? ''}
                          onChange={(e) => setReason({ ...reason, [f.id]: e.target.value })}
                        />
                        <Confirm
                          label="Zrušit"
                          question="Zrušit označení? Držené zprávy pak odejdou, pokud jsou ještě v platnosti."
                          confirmLabel="Zrušit označení"
                          onConfirm={() =>
                            (reason[f.id] ?? '').trim()
                              ? clear.mutate({ id: f.id, reason: reason[f.id] as string })
                              : setError('Důvod zrušení je povinný.')
                          }
                        />
                      </div>
                    )}
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.items.length === 0 ? (
        <EmptyState title={all ? 'Žádné označení' : 'Žádné aktivní označení'}>
          Označení vznikne přijetím návrhu nebo z karty zákazníka. Dokud žádné není, hub drží zprávy
          jen podle běžných pravidel.
        </EmptyState>
      ) : null}
    </Panel>
  );
}

function Suggestions({ canManage }: { canManage: boolean }): JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [kind, setKind] = useState('problem');
  const list = useQuery<{ items: SuggestionRow[] }>({
    queryKey: ['care-suggestions'],
    queryFn: () => api.get('/api/care/suggestions?status=open'),
  });
  const done = (): void => {
    setError(null);
    void client.invalidateQueries({ queryKey: ['care-suggestions'] });
    void client.invalidateQueries({ queryKey: ['care-flags'] });
  };
  const accept = useMutation({
    mutationFn: (i: { id: number; scope: 'race' | 'all' }) =>
      api.post(`/api/care/suggestions/${i.id}/accept`, { kind, scope: i.scope }),
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });
  const dismiss = useMutation({
    mutationFn: (id: number) => api.post(`/api/care/suggestions/${id}/dismiss`, {}),
    onSuccess: done,
    onError: (e: Error) => setError(e.message),
  });
  return (
    <Panel
      title="Návrhy označení"
      subtitle="Hub navrhne označit zákazníka, který byl nespokojený nebo se ozval podpoře těsně u závodu. Nic se nestane, dokud nerozhodnete."
    >
      {error ? <ErrorState title="Návrh se nepodařilo vyřídit" error={new Error(error)} /> : null}
      <div className="s2a-filters">
        <Field label="Druh označení při přijetí návrhu">
          <select value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(CARE_KIND_LABEL).map(([v, t]) => (
              <option key={v} value={v}>
                {t}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {list.isLoading ? <LoadingState what="návrhy" /> : null}
      {list.isError ? (
        <ErrorState
          title="Návrhy se nepodařilo načíst"
          error={list.error}
          onRetry={() => void list.refetch()}
        />
      ) : null}
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Zákazník</th>
              <th>Důvod</th>
              <th>Závod</th>
              <th>Vzniklo</th>
              {canManage ? <th>Akce</th> : null}
            </tr>
          </thead>
          <tbody>
            {list.data?.items.map((s) => (
              <tr key={s.id} data-testid={`suggestion-${s.id}`}>
                <td>
                  <a href={hrefFor('customers', s.contact_id)}>{s.email}</a>
                  <div className="muted">{s.brand}</div>
                </td>
                <td>{label(SUGGESTION_REASON_LABEL, s.reason)}</td>
                <td>{s.race_name ?? '—'}</td>
                <td>{dateTime(s.created_at)}</td>
                {canManage ? (
                  <td>
                    <div className="row">
                      {s.race_id ? (
                        <button
                          className="btn pri"
                          onClick={() => accept.mutate({ id: s.id, scope: 'race' })}
                        >
                          Označit k závodu
                        </button>
                      ) : null}
                      <button
                        className="btn"
                        onClick={() => accept.mutate({ id: s.id, scope: 'all' })}
                      >
                        Označit všechny závody
                      </button>
                      <button className="btn" onClick={() => dismiss.mutate(s.id)}>
                        Odmítnout
                      </button>
                    </div>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.data && list.data.items.length === 0 ? (
        <EmptyState title="Žádné návrhy nečekají">
          Hub navrhne označení, když je zákazník nespokojený nebo se ozve podpoře těsně před
          závodem.
        </EmptyState>
      ) : null}
    </Panel>
  );
}

function ImportCases({ canManage }: { canManage: boolean }): JSX.Element {
  const [csv, setCsv] = useState('');
  const [askImport, setAskImport] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{
    dryRun: boolean;
    created: number;
    rows: Array<{ line: number; email: string; result: string }>;
  } | null>(null);
  const run = useMutation({
    mutationFn: (dryRun: boolean) =>
      api.post<NonNullable<typeof result>>('/api/care/cases/import', { csv, dryRun }),
    onSuccess: (r) => {
      setError(null);
      setResult(r);
    },
    onError: (e: Error) => setError(e.message),
  });
  if (!canManage)
    return (
      <EmptyState title="Import je jen pro péči o zákazníky">
        Import případů smí provést péče o zákazníky a administrátor. Vy máte obrazovku jen ke čtení.
      </EmptyState>
    );
  const ok = result?.rows.filter((r) => r.result === 'ok').length ?? 0;
  return (
    <Panel
      title="Import případů z CSV"
      subtitle="Soubor CSV musí mít sloupec email nebo order_code (kód objednávky). Volitelné sloupce: topic (téma), status (open nebo resolved), resolution (řešení), satisfaction (spokojenost), opened_at (kdy vzniklo). Nejdřív se ukáže náhled, teprve potom se zapíše."
    >
      {error ? <ErrorState title="Import se nepovedl" error={new Error(error)} /> : null}
      <input
        type="file"
        accept=".csv,text/csv"
        aria-label="Soubor CSV"
        onChange={async (e) => {
          const f = e.target.files?.[0];
          if (f) setCsv(await f.text());
        }}
      />
      <textarea
        rows={6}
        className="s2a-wide"
        aria-label="Obsah CSV"
        value={csv}
        onChange={(e) => setCsv(e.target.value)}
      />
      <div className="row">
        <button className="btn" disabled={!csv || run.isPending} onClick={() => run.mutate(true)}>
          Zkontrolovat (náhled)
        </button>
        <button
          className="btn pri"
          disabled={!result || ok === 0}
          onClick={() => setAskImport(true)}
        >
          Importovat
        </button>
      </div>
      <ConfirmDialog
        open={askImport}
        title="Zapsat případy?"
        confirmLabel="Importovat"
        busy={run.isPending}
        onCancel={() => setAskImport(false)}
        onConfirm={() => {
          run.mutate(false);
          setAskImport(false);
        }}
      >
        Zapíše se {ok} platných případů z poslední kontroly.
      </ConfirmDialog>
      {result ? (
        <>
          <p className="note">
            {result.dryRun
              ? `Náhled: ${ok} řádků v pořádku z ${result.rows.length}.`
              : `Zapsáno ${result.created} případů.`}
          </p>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th className="n">Řádek</th>
                  <th>Zákazník</th>
                  <th>Výsledek</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.map((r) => (
                  <tr key={r.line}>
                    <td className="n">{r.line}</td>
                    <td>{r.email}</td>
                    <td>
                      <StatusBadge
                        tone={r.result === 'ok' ? 'ok' : 'warn'}
                        icon={r.result === 'ok' ? 'check' : 'alert'}
                      >
                        {label(IMPORT_RESULT_LABEL, r.result)}
                      </StatusBadge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </Panel>
  );
}
